import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const LEVELS = [
  { id: 'beginner', name: 'Beginner' },
  { id: 'intermediate', name: 'Intermediate' },
  { id: 'advanced', name: 'Advanced' },
];

import { PrerequisiteCoursePicker } from './PrerequisiteCoursePicker';

const CourseSettingsModal = ({ open, onClose, course, onSave, saving }) => {
  const { api, user } = useAuth();
  const canEditPricing = user?.role === 'super_admin';
  const [subscriptionTiers, setSubscriptionTiers] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableBundles, setAvailableBundles] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    short_description: '',
    category: 'Other',
    categories: [],
    thumbnail_url: '',
    price: 0,
    currency: 'GBP',
    level: 'beginner',
    duration_hours: null,
    bundle_ids: [],
    is_bundle_only: false,
    is_free: false,
    is_subscription_only: false,
    badges: [],
    is_accredited: false,
    accreditation_body: '',
    included_in_subscription_tiers: [],
    tags: [],
    learning_outcomes: [],
    prerequisites: [],
    prerequisite_course_ids: [],
    thumbnail_alt: '',
    target_audience: '',
    instructor_name: '',
    instructor_bio: '',
    faqs: [],
    meta_title: '',
    meta_description: '',
    meta_keywords: [],
    is_mandatory: false,
    course_type: 'staff',
  });

  const [newTag, setNewTag] = useState('');
  const [newOutcome, setNewOutcome] = useState('');
  const [newPrereq, setNewPrereq] = useState('');
  const [newKeyword, setNewKeyword] = useState('');

  useEffect(() => {
    if (open) {
      Promise.allSettled([
        api.get('/admin/subscription-tiers'),
        api.get('/categories'),
        api.get('/bundles/admin/all'),
      ]).then(([tiersRes, catsRes, bundlesRes]) => {
        if (tiersRes.status === 'fulfilled') setSubscriptionTiers(tiersRes.value.data || []);
        if (catsRes.status === 'fulfilled') setAvailableCategories(catsRes.value.data || []);
        if (bundlesRes.status === 'fulfilled') {
          const allBundles = bundlesRes.value.data || [];
          setAvailableBundles(allBundles);
          // Resolve which bundles contain this course (if course.bundle_ids not stored on doc)
          if (course?.id && (!course.bundle_ids || course.bundle_ids.length === 0)) {
            const resolvedIds = allBundles
              .filter(b => (b.course_ids || []).includes(course.id))
              .map(b => b.id);
            if (resolvedIds.length > 0) {
              setFormData(prev => ({ ...prev, bundle_ids: resolvedIds }));
            }
          }
        }
      });
    }
  }, [open, api, course?.id]);

  useEffect(() => {
    if (course) {
      setFormData({
        title: course.title || '',
        description: course.description || '',
        short_description: course.short_description || '',
        category: course.category || 'Other',
        categories: course.categories?.length ? course.categories : (course.id && course.category ? [course.category] : []),
        thumbnail_url: course.thumbnail_url || '',
        price: course.price || 0,
        currency: course.currency || 'GBP',
        level: course.level || 'beginner',
        duration_hours: course.duration_hours || null,
        bundle_ids: course.bundle_ids || [],
        is_bundle_only: course.is_bundle_only || false,
        is_free: course.is_free || (course.price <= 0 && !course.is_bundle_only && !course.is_subscription_only) || false,
        is_subscription_only: course.is_subscription_only || course.is_bundle_only || false,
        badges: course.badges || [],
        is_accredited: course.is_accredited || false,
        accreditation_body: course.accreditation_body || '',
        included_in_subscription_tiers: course.included_in_subscription_tiers || [],
        tags: course.tags || [],
        learning_outcomes: course.learning_outcomes || [],
        prerequisites: course.prerequisites || [],
        prerequisite_course_ids: course.prerequisite_course_ids || [],
        thumbnail_alt: course.thumbnail_alt || '',
        target_audience: course.target_audience || '',
        instructor_name: course.instructor_name || '',
        instructor_bio: course.instructor_bio || '',
        faqs: course.faqs || [],
        meta_title: course.meta_title || '',
        meta_description: course.meta_description || '',
        meta_keywords: course.meta_keywords || [],
        is_mandatory: course.is_mandatory || false,
        course_type: course.course_type || 'staff',
      });
    }
  }, [course]);

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const addItem = (key, value, setter) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [key]: [...prev[key], value.trim()],
      }));
      setter('');
    }
  };

  const removeItem = (key, index) => {
    setFormData(prev => ({
      ...prev,
      [key]: prev[key].filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = () => {
    if (!formData.title.trim()) {
      toast.error('Course title is required');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Course description is required — even a brief summary helps');
      return;
    }
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh]" onInteractOutside={(e) => { if (e.target?.closest?.("[data-testid=cookie-banner]")) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle>
            {course?.id ? 'Course Settings' : 'Create New Course'}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[65vh]">
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="basic">Basic</TabsTrigger>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="pricing">Pricing</TabsTrigger>
              <TabsTrigger value="instructor">Info</TabsTrigger>
              <TabsTrigger value="seo">SEO</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Course Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="e.g., Mental Health Awareness Training"
                  data-testid="course-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Short Description</Label>
                <Textarea
                  value={formData.short_description}
                  onChange={(e) => handleChange('short_description', e.target.value)}
                  placeholder="Brief summary shown in course cards (max 200 characters)"
                  rows={2}
                  maxLength={200}
                />
              </div>

              <div className="space-y-2">
                <Label>Full Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Detailed course description..."
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Categories</Label>
                  <div className="max-h-36 overflow-y-auto border border-slate-200 rounded-lg p-2 space-y-1">
                    {availableCategories.length > 0 ? availableCategories.map(cat => {
                      const selected = (formData.categories || []).includes(cat.name);
                      return (
                        <label key={cat.id} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm hover:bg-slate-50 ${selected ? 'bg-[#20acf6]/5 font-medium' : ''}`}>
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => {
                              const cats = formData.categories || [];
                              const updated = selected ? cats.filter(c => c !== cat.name) : [...cats, cat.name];
                              handleChange('categories', updated);
                              handleChange('category', updated[0] || 'Other');
                            }}
                            className="accent-[#20acf6] h-3.5 w-3.5"
                          />
                          <span>{cat.name}</span>
                        </label>
                      );
                    }) : (
                      <p className="text-xs text-slate-400 p-2">No categories found. Create them in Admin &gt; Categories.</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Level</Label>
                  <Select
                    value={formData.level}
                    onValueChange={(v) => handleChange('level', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LEVELS.map((level) => (
                        <SelectItem key={level.id} value={level.id}>
                          {level.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Course Type */}
              <div className="space-y-2">
                <Label>Course Type</Label>
                <Select
                  value={formData.course_type}
                  onValueChange={(v) => handleChange('course_type', v)}
                >
                  <SelectTrigger data-testid="course-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff Training</SelectItem>
                    <SelectItem value="resident">Resident Course (Independence Builder)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {formData.course_type === 'resident'
                    ? 'This course will appear in the Resident Courses section and is typically set to free.'
                    : 'Standard staff training course for care professionals.'}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Featured Image *</Label>
                <div className="space-y-3">
                  {formData.thumbnail_url && (
                    <div className="relative w-full aspect-video rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
                      <img src={formData.thumbnail_url} alt="Course thumbnail" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => handleChange('thumbnail_url', '')}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-[#20acf6] hover:bg-[#3bb8f7] text-white rounded-md text-sm font-medium transition-colors" data-testid="upload-featured-image-btn">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      Upload Image
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const fd = new FormData();
                            fd.append('file', file);
                            fd.append('alt_text', formData.title || 'Course image');
                            const res = await api.post('/media/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                            handleChange('thumbnail_url', res.data.url);
                            toast.success('Image uploaded');
                          } catch {
                            toast.error('Upload failed');
                          }
                        }}
                      />
                    </label>
                    <span className="text-xs text-slate-400 self-center">or</span>
                    <Input
                      value={formData.thumbnail_url}
                      onChange={(e) => handleChange('thumbnail_url', e.target.value)}
                      placeholder="Paste image URL..."
                      className="flex-1"
                      data-testid="thumbnail-url-input"
                    />
                  </div>
                  <p className="text-xs text-slate-400">All courses should have a featured image. JPG, PNG or WebP, max 10MB.</p>
                  <Label className="text-xs">Thumbnail alt text</Label>
                  <Input value={formData.thumbnail_alt} onChange={(e) => handleChange('thumbnail_alt', e.target.value)}
                    placeholder="Describe the image, e.g. Care worker supporting a resident" data-testid="thumbnail-alt-input" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Tags</Label>
                <div className="flex gap-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add a tag..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addItem('tags', newTag, setNewTag);
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => addItem('tags', newTag, setNewTag)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-sm"
                    >
                      {tag}
                      <button
                        onClick={() => removeItem('tags', idx)}
                        className="hover:text-red-600"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="details" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Duration (hours)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={formData.duration_hours || ''}
                  onChange={(e) => handleChange('duration_hours', parseFloat(e.target.value) || null)}
                  placeholder="Auto-calculated based on content"
                />
              </div>

              {/* Compliance & Accreditation */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-sm text-[#2a3e5d] mb-3">Compliance & Accreditation</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Expires After (months)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.expires_after_months || ''}
                      onChange={(e) => handleChange('expires_after_months', parseInt(e.target.value) || null)}
                      placeholder="e.g. 12"
                    />
                    <p className="text-xs text-slate-500">Set to 0 or blank for no expiry</p>
                  </div>
                  <div className="space-y-2">
                    <Label>CPD Hours</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.5"
                      value={formData.cpd_hours || ''}
                      onChange={(e) => handleChange('cpd_hours', parseFloat(e.target.value) || null)}
                      placeholder="e.g. 2.5"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <Label className="text-sm">Mandatory Training</Label>
                  <Switch
                    checked={formData.is_mandatory || false}
                    onCheckedChange={(v) => handleChange('is_mandatory', v)}
                  />
                </div>
                <div className="flex items-center justify-between mt-3">
                  <Label className="text-sm">CPD Accredited</Label>
                  <Switch
                    checked={formData.is_accredited || false}
                    onCheckedChange={(v) => handleChange('is_accredited', v)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Learning Outcomes</Label>
                <div className="flex gap-2">
                  <Input
                    value={newOutcome}
                    onChange={(e) => setNewOutcome(e.target.value)}
                    placeholder="What will learners achieve?"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addItem('learning_outcomes', newOutcome, setNewOutcome);
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => addItem('learning_outcomes', newOutcome, setNewOutcome)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <ul className="space-y-1 mt-2">
                  {formData.learning_outcomes.map((outcome, idx) => (
                    <li
                      key={idx}
                      className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm"
                    >
                      <span>{outcome}</span>
                      <button
                        onClick={() => removeItem('learning_outcomes', idx)}
                        className="hover:text-red-600"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <PrerequisiteCoursePicker courseId={course?.id} value={formData.prerequisite_course_ids} onChange={(v) => handleChange('prerequisite_course_ids', v)} />

              <div className="space-y-2">
                <Label>Prerequisites (free-text notes shown to learners)</Label>
                <div className="flex gap-2">
                  <Input
                    value={newPrereq}
                    onChange={(e) => setNewPrereq(e.target.value)}
                    placeholder="What should learners know beforehand?"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addItem('prerequisites', newPrereq, setNewPrereq);
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => addItem('prerequisites', newPrereq, setNewPrereq)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <ul className="space-y-1 mt-2">
                  {formData.prerequisites.map((prereq, idx) => (
                    <li
                      key={idx}
                      className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm"
                    >
                      <span>{prereq}</span>
                      <button
                        onClick={() => removeItem('prerequisites', idx)}
                        className="hover:text-red-600"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label>Accredited Course</Label>
                  <p className="text-xs text-slate-500 mt-1">
                    Mark if this course is accredited by a professional body
                  </p>
                </div>
                <Switch
                  checked={formData.is_accredited}
                  onCheckedChange={(v) => handleChange('is_accredited', v)}
                />
              </div>

              {formData.is_accredited && (
                <div className="space-y-2">
                  <Label>Accreditation Body</Label>
                  <Input
                    value={formData.accreditation_body}
                    onChange={(e) => handleChange('accreditation_body', e.target.value)}
                    placeholder="e.g., CPD Certified"
                  />
                </div>
              )}

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label>Mandatory Course</Label>
                  <p className="text-xs text-slate-500 mt-1">
                    Required for CQC compliance — tracked in organisation reports
                  </p>
                </div>
                <Switch
                  checked={formData.is_mandatory || false}
                  onCheckedChange={(v) => handleChange('is_mandatory', v)}
                  data-testid="mandatory-toggle"
                />
              </div>

              {/* Admin Badges */}
              <div className="space-y-3 mt-4 pt-4 border-t border-slate-200">
                <Label>Course Badges</Label>
                <p className="text-xs text-slate-500">Select manual badges. FREE, NEW, POPULAR, and CPD ACCREDITED are assigned automatically based on course data.</p>
                <div className="flex flex-wrap gap-2">
                  {['RECOMMENDED', 'COMPLIANCE', 'STAFF PICK', 'BEST SELLER', 'DISCOUNTED'].map(badge => {
                    const selected = (formData.badges || []).includes(badge);
                    const colors = { RECOMMENDED: '#10B981', COMPLIANCE: '#EF4444', 'STAFF PICK': '#0D9488', 'BEST SELLER': '#D97706', DISCOUNTED: '#20acf6' };
                    return (
                      <button
                        key={badge}
                        type="button"
                        onClick={() => {
                          const current = formData.badges || [];
                          handleChange('badges', selected ? current.filter(b => b !== badge) : [...current, badge]);
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-all ${
                          selected ? 'text-white' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                        }`}
                        style={selected ? { backgroundColor: colors[badge], borderColor: colors[badge] } : undefined}
                        data-testid={`badge-toggle-${badge.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {badge}
                      </button>
                    );
                  })}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="pricing" className="space-y-4 mt-4">
              {!canEditPricing && (
                <p className="text-sm bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-slate-600" data-testid="pricing-readonly-note">
                  Only a Super Admin can change pricing, bundles and subscription access.
                </p>
              )}
              <fieldset disabled={!canEditPricing} className="space-y-4 disabled:opacity-60">
              {/* Free Toggle */}
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                <div>
                  <Label className="text-green-800 font-semibold">Free Course</Label>
                  <p className="text-xs text-green-700 mt-1">
                    When enabled, the course is free for everyone. Overrides all other pricing.
                  </p>
                </div>
                <Switch
                  checked={formData.is_free || false}
                  onCheckedChange={(v) => {
                    handleChange('is_free', v);
                    if (v) {
                      handleChange('price', 0);
                      handleChange('is_subscription_only', false);
                    }
                  }}
                  data-testid="free-toggle"
                />
              </div>

              {/* Subscription Only Toggle */}
              <div className={`flex items-center justify-between p-4 rounded-lg border ${formData.is_free ? 'bg-slate-50 border-slate-200 opacity-50' : 'bg-amber-50 border-amber-200'}`}>
                <div>
                  <Label className={formData.is_free ? 'text-slate-500' : 'text-amber-800 font-semibold'}>Subscription Only</Label>
                  <p className="text-xs text-slate-500 mt-1">
                    When enabled, course can only be accessed via an organisation subscription. Not available for individual purchase or bundles.
                  </p>
                </div>
                <Switch
                  checked={formData.is_subscription_only || false}
                  onCheckedChange={(v) => {
                    handleChange('is_subscription_only', v);
                    if (v) handleChange('price', 0);
                  }}
                  disabled={formData.is_free}
                  data-testid="subscription-only-toggle"
                />
              </div>

              {/* Individual Price */}
              {!formData.is_free && !formData.is_subscription_only && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Individual Price</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => handleChange('price', parseFloat(e.target.value) || 0)}
                      placeholder="0.00 (leave blank for no individual purchase)"
                      data-testid="individual-price-input"
                    />
                    <p className="text-xs text-slate-500">Set to 0 or leave blank if course is only available via bundles or subscription</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <Select
                      value={formData.currency}
                      onValueChange={(v) => handleChange('currency', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GBP">GBP (£)</SelectItem>
                        <SelectItem value="USD">USD ($)</SelectItem>
                        <SelectItem value="EUR">EUR (€)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Included in Bundles */}
              {!formData.is_subscription_only && !formData.is_free && (
                <div className="space-y-3">
                  <Label>Included in Bundles</Label>
                  <p className="text-xs text-slate-500">Select which bundles this course belongs to</p>
                  <div className="space-y-2 p-3 border border-slate-200 rounded-lg max-h-40 overflow-y-auto">
                    {availableBundles.length > 0 ? availableBundles.map((bundle) => {
                      const selected = (formData.bundle_ids || []).includes(bundle.id);
                      return (
                        <label key={bundle.id} className={`flex items-center gap-3 px-2 py-1.5 rounded cursor-pointer text-sm hover:bg-slate-50 ${selected ? 'bg-[#02d1d4]/5' : ''}`}>
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => {
                              const ids = formData.bundle_ids || [];
                              handleChange('bundle_ids', selected ? ids.filter(id => id !== bundle.id) : [...ids, bundle.id]);
                            }}
                            className="accent-[#02d1d4] h-3.5 w-3.5"
                          />
                          <span>{bundle.title}</span>
                          {bundle.category && <Badge variant="outline" className="text-[10px] ml-auto">{bundle.category}</Badge>}
                          <span className="text-xs text-slate-400 ml-auto">£{bundle.full_price}</span>
                        </label>
                      );
                    }) : (
                      <p className="text-sm text-slate-400 italic">No bundles created yet</p>
                    )}
                  </div>
                </div>
              )}

              {/* Subscription Tiers */}
              {!formData.is_free && (
                <div className="space-y-3">
                  <Label>Included in Subscription Tiers</Label>
                  <p className="text-xs text-slate-500">
                    Select which organisation subscription tiers include this course
                  </p>
                  <div className="space-y-2 p-3 border border-slate-200 rounded-lg max-h-48 overflow-y-auto">
                    {subscriptionTiers.length > 0 ? subscriptionTiers.map((tier) => (
                      <div key={tier.id} className="flex items-center gap-3">
                        <Checkbox
                          id={`tier-${tier.id}`}
                          checked={formData.included_in_subscription_tiers.includes(tier.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              handleChange('included_in_subscription_tiers', [
                                ...formData.included_in_subscription_tiers,
                                tier.id,
                              ]);
                            } else {
                              handleChange('included_in_subscription_tiers',
                                formData.included_in_subscription_tiers.filter(id => id !== tier.id)
                              );
                            }
                          }}
                          data-testid={`tier-checkbox-${tier.id}`}
                        />
                        <label htmlFor={`tier-${tier.id}`} className="text-sm cursor-pointer">
                          {tier.name} <span className="text-slate-500">(£{tier.price_monthly}/mo)</span>
                        </label>
                      </div>
                    )) : (
                      <p className="text-sm text-slate-400 italic">No subscription tiers available</p>
                    )}
                  </div>
                </div>
              )}

              {/* Pricing Summary */}
              <div className="p-4 bg-blue-50 rounded-lg text-sm text-blue-800">
                <p className="font-medium">Pricing Summary:</p>
                <div className="mt-2 space-y-1 text-blue-700">
                  {formData.is_free && <p>This course is <strong>free</strong> for all learners.</p>}
                  {formData.is_subscription_only && !formData.is_free && <p>This course is <strong>subscription only</strong>. Learners must subscribe via their organisation.</p>}
                  {!formData.is_free && !formData.is_subscription_only && formData.price > 0 && <p>Individual price: <strong>£{formData.price.toFixed(2)}</strong></p>}
                  {!formData.is_free && !formData.is_subscription_only && (formData.bundle_ids || []).length > 0 && <p>Part of <strong>{formData.bundle_ids.length} bundle{formData.bundle_ids.length !== 1 ? 's' : ''}</strong></p>}
                  {!formData.is_free && formData.included_in_subscription_tiers.length > 0 && <p>Included in <strong>{formData.included_in_subscription_tiers.length} subscription tier{formData.included_in_subscription_tiers.length !== 1 ? 's' : ''}</strong></p>}
                </div>
              </div>
              </fieldset>
            </TabsContent>

            {/* Instructor & FAQs Tab */}
            <TabsContent value="instructor" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Target Audience</Label>
                <Textarea
                  value={formData.target_audience}
                  onChange={(e) => handleChange('target_audience', e.target.value)}
                  placeholder="e.g., New care workers, support staff, team leaders"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Instructor Name</Label>
                <Input
                  value={formData.instructor_name}
                  onChange={(e) => handleChange('instructor_name', e.target.value)}
                  placeholder="e.g., Dr. Sarah Mitchell"
                />
              </div>

              <div className="space-y-2">
                <Label>Instructor Bio</Label>
                <Textarea
                  value={formData.instructor_bio}
                  onChange={(e) => handleChange('instructor_bio', e.target.value)}
                  placeholder="Short biography..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>FAQs</Label>
                {formData.faqs.map((faq, idx) => (
                  <div key={idx} className="p-3 bg-slate-50 rounded-lg space-y-2">
                    <div className="flex items-center gap-2">
                      <Input
                        value={faq.question}
                        onChange={(e) => {
                          const updated = [...formData.faqs];
                          updated[idx] = { ...updated[idx], question: e.target.value };
                          handleChange('faqs', updated);
                        }}
                        placeholder="Question"
                        className="text-sm"
                      />
                      <button
                        onClick={() => handleChange('faqs', formData.faqs.filter((_, i) => i !== idx))}
                        className="text-slate-400 hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <Textarea
                      value={faq.answer}
                      onChange={(e) => {
                        const updated = [...formData.faqs];
                        updated[idx] = { ...updated[idx], answer: e.target.value };
                        handleChange('faqs', updated);
                      }}
                      placeholder="Answer"
                      rows={2}
                      className="text-sm"
                    />
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleChange('faqs', [...formData.faqs, { question: '', answer: '' }])}
                >
                  <Plus className="h-4 w-4 mr-1" /> Add FAQ
                </Button>
              </div>
            </TabsContent>

            {/* SEO Tab */}
            <TabsContent value="seo" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Meta Title</Label>
                <Input
                  value={formData.meta_title}
                  onChange={(e) => handleChange('meta_title', e.target.value)}
                  placeholder="SEO page title (defaults to course title)"
                  maxLength={70}
                />
                <p className="text-xs text-slate-400">{(formData.meta_title || '').length}/70</p>
              </div>

              <div className="space-y-2">
                <Label>Meta Description</Label>
                <Textarea
                  value={formData.meta_description}
                  onChange={(e) => handleChange('meta_description', e.target.value)}
                  placeholder="SEO description for search engines"
                  rows={3}
                  maxLength={160}
                />
                <p className="text-xs text-slate-400">{(formData.meta_description || '').length}/160</p>
              </div>

              <div className="space-y-2">
                <Label>Meta Keywords</Label>
                <div className="flex gap-2">
                  <Input
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    placeholder="Add keyword..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addItem('meta_keywords', newKeyword, setNewKeyword);
                      }
                    }}
                  />
                  <Button type="button" variant="outline" onClick={() => addItem('meta_keywords', newKeyword, setNewKeyword)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.meta_keywords.map((kw, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-sm">
                      {kw}
                      <button onClick={() => removeItem('meta_keywords', idx)} className="hover:text-red-600">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg text-sm text-blue-800">
                <p className="font-medium">SEO Tips:</p>
                <ul className="list-disc list-inside mt-2 space-y-1 text-blue-700">
                  <li>Keep title under 70 characters for search engines</li>
                  <li>Description should be 120-160 characters</li>
                  <li>Use keywords your learners would search for</li>
                </ul>
              </div>
            </TabsContent>
          </Tabs>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || !formData.title.trim() || !formData.description.trim()}
            className="bg-[#20acf6] hover:bg-[#3bb8f7]"
            data-testid="save-course-btn"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              course?.id ? 'Save Changes' : 'Create Course'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CourseSettingsModal;
